import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ticketsApi } from '../api'
import { ErrorBox, EmptyState } from '../hooks/useApi.jsx'
import { Badge, Loader } from '../ui'
import { HiArrowRight, HiArrowPath } from 'react-icons/hi2'

const STATUS_LABEL = {
  new: 'Yangi', accepted: 'Qabul', in_progress: 'Jarayonda',
  waiting: 'Kutmoqda', resolved: 'Yechildi', closed: 'Yopildi'
}
// Bosqich (yangi / jarayonda / yopildi) — neytral: bu shoshilinchlik emas.
// Rang faqat muhimlikda va yakuniy "yechildi" signalida (DESIGN.md 4).
const STATUS_TONE = {
  new: 'neutral', accepted: 'neutral', in_progress: 'neutral',
  waiting: 'neutral', resolved: 'ok', closed: 'neutral'
}

const COLUMNS = ['MUROJAAT №', 'FUQARO', 'OPERATOR', 'MUAMMO', 'HOLAT', 'SANA', '']

export default function Kritik() {
  const navigate    = useNavigate()
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const abortRef = useRef(null)

  const load = useCallback(async () => {
    // Oldingi sekin so'rov yangi holatni ustidan yozmasligi uchun bekor qilamiz
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true); setError(null)
    try {
      const res = await ticketsApi.list({ priority: 'critical', limit: 50 }, { signal: ctrl.signal })
      if (ctrl.signal.aborted) return
      setTickets(res.items || [])
    } catch(e) {
      if (e?.name === 'AbortError' || ctrl.signal.aborted) return
      setError(e.message)
    } finally {
      if (!ctrl.signal.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const iv = setInterval(load, 10000)
    return () => { clearInterval(iv); abortRef.current?.abort() }
  }, [load])

  const active   = tickets.filter(t => !['resolved','closed'].includes(t.status))
  const resolved = tickets.filter(t => ['resolved','closed'].includes(t.status))

  const open = (id) => navigate(`/ticket/${id}`)
  // Kit Table naqshi: bosiladigan qator klaviatura bilan ham ochiladi
  const rowKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      open(id)
    }
  }

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="page-hdr">
        <div className="page-hdr-left">
          <h2>Kritik murojaatlar</h2>
          <p>Shoshilinch e'tibor talab qiluvchi murojaatlar</p>
        </div>
        <div className="page-hdr-right">
          <button className="btn btn-secondary btn-sm" onClick={load}>
            <HiArrowPath aria-hidden="true" /> Yangilash
          </button>
        </div>
      </div>

      <ErrorBox error={error} onRetry={load} />

      {loading ? (
        // Kritiklar ro'yxati saralanib olinmoqda — "searching"
        <Loader state="searching" size="lg" block />
      ) : (
        <>
          {/* Faol kritik ticketlar */}
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'var(--r-lg)', marginBottom:24, overflow:'hidden'
          }}>
            <div style={{
              padding:'12px 16px',
              borderBottom:'1px solid var(--border)',
              display:'flex', alignItems:'center', gap:8,
              background:'var(--surface-2)'
            }}>
              <Badge tone="critical">Kritik</Badge>
              <span style={{ fontWeight:600, color:'var(--text-1)', fontSize:14 }}>
                Faol — {active.length} ta
              </span>
            </div>

            {active.length === 0 ? (
              <EmptyState text="Faol kritik murojaat yo'q" subtext="Hozircha shoshilinch e'tibor talab qiluvchi faol murojaat mavjud emas." />
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)', background:'var(--surface-2)' }}>
                    {COLUMNS.map((h, i) => (
                      <th key={h || `col-${i}`} scope="col" style={{
                        padding:'10px 16px', textAlign:'left',
                        fontSize:11, fontWeight:600,
                        color:'var(--text-2)', letterSpacing:'.06em'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {active.map(t => (
                    <tr key={t.id}
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={e => rowKeyDown(e, t.id)}
                      style={{ borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background var(--dur-1) var(--ease)' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}
                      onFocus={e => e.currentTarget.style.background='var(--surface-2)'}
                      onBlur={e => e.currentTarget.style.background='transparent'}
                    >
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ fontFamily:'var(--mono)', fontWeight:600, color:'var(--text-1)', fontSize:13 }}>
                          #{t.ticket_number}
                        </span>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--text-1)' }}>
                          {t.citizen_name || 'Fuqaro'}
                        </div>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        {t.operator_name ? (
                          <span style={{ fontSize:11, fontWeight:500, padding:'3px 8px',
                            borderRadius:'var(--r)', background:'var(--surface-2)',
                            color:'var(--text-2)', border:'1px solid var(--border)',
                            whiteSpace:'nowrap' }}>
                            {t.operator_name}
                          </span>
                        ) : (
                          <span style={{ fontSize:11, color:'var(--text-3)' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding:'12px 16px', maxWidth:280 }}>
                        <div style={{ fontSize:12, color:'var(--text-2)',
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {t.description}
                        </div>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <Badge tone={STATUS_TONE[t.status] || 'neutral'}>
                          {STATUS_LABEL[t.status] || t.status}
                        </Badge>
                      </td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:'var(--text-3)', whiteSpace:'nowrap' }}>
                        {t.created_at ? new Date(t.created_at).toLocaleString('uz-UZ', {
                          day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'
                        }) : '—'}
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <button
                          onClick={e => { e.stopPropagation(); open(t.id) }}
                          style={{ padding:'6px 12px', background:'var(--text-1)', border:'none',
                            borderRadius:'var(--r)', color:'var(--surface)', fontWeight:600, fontSize:12,
                            cursor:'pointer', fontFamily:'inherit', display:'inline-flex',
                            alignItems:'center', gap:6 }}>
                          Ochish <HiArrowRight aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Yechildi */}
          {resolved.length > 0 && (
            <div style={{
              background:'var(--surface)', border:'1px solid var(--border)',
              borderRadius:'var(--r-lg)', overflow:'hidden'
            }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)',
                fontSize:13, fontWeight:600, color:'var(--text-3)' }}>
                Yechildi — {resolved.length} ta
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                  {resolved.map(t => (
                    <tr key={t.id}
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={e => rowKeyDown(e, t.id)}
                      style={{ borderBottom:'1px solid var(--border)', cursor:'pointer',
                        opacity:.6, transition:'opacity var(--dur-1) var(--ease)' }}
                      onMouseEnter={e => e.currentTarget.style.opacity='1'}
                      onMouseLeave={e => e.currentTarget.style.opacity='.6'}
                      onFocus={e => e.currentTarget.style.opacity='1'}
                      onBlur={e => e.currentTarget.style.opacity='.6'}
                    >
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--text-2)' }}>
                          #{t.ticket_number}
                        </span>
                      </td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:'var(--text-2)', maxWidth:300,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {t.description}
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <Badge tone="ok">Yechildi</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
